export const BLOG_POSTS = [
  {
    slug: "what-is-air-bubble-insulation",
    title: "What is Air Bubble Insulation? A No-Nonsense Guide",
    excerpt:
      "How reflective bubble foil actually stops heat — and where it beats traditional wool and boards.",
    date: "2026-01-08",
    readTime: "6 min read",
    category: "Guide",
    cover:
      "https://images.unsplash.com/photo-1705254772179-991469a47e19?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "Air bubble insulation is a multi-layer product built from two sheets of pure aluminium foil bonded to a sealed polyethylene bubble core. It targets radiant heat — the dominant form of heat transfer through metal roofs — and reflects up to 97% of it before it enters your building.",
      },
      { type: "h", text: "How it works" },
      {
        type: "p",
        text: "Radiant heat travels through air as infrared energy. Traditional fibreglass and mineral wool try to slow this heat by trapping air. Reflective foil takes a different approach: it bounces the infrared back where it came from. Combined with the still-air pocket of the bubble core, you get both radiant blocking and conductive slowdown in a single 6mm layer.",
      },
      { type: "h", text: "Where it wins" },
      {
        type: "ul",
        items: [
          "Metal-roofed warehouses & industrial sheds",
          "Poultry farms (comfort + weight gain)",
          "Cold storage envelopes",
          "HVAC duct wrapping",
          "Retro-fit projects where thickness is limited",
        ],
      },
      { type: "h", text: "Where it doesn't" },
      {
        type: "p",
        text: "Air bubble insulation is not a structural or acoustic solution. For high-noise metal roofs, pair it with XLPE. For deep-cold cold rooms, use XLPE or PU as the primary layer and foil bubble as a radiant liner.",
      },
      { type: "h", text: "Buying checklist" },
      {
        type: "ul",
        items: [
          "Confirm foil is pure aluminium, not metallised film",
          "Ask for reflectivity ≥ 95% and emissivity ≤ 0.05",
          "Insist on sealed bubble core (not open-cell)",
          "Verify fire rating (Class B minimum)",
        ],
      },
    ],
  },
  {
    slug: "xlpe-vs-air-bubble-insulation",
    title: "XLPE vs Air Bubble Insulation — Which One Do You Actually Need?",
    excerpt:
      "A direct comparison across thermal, acoustic, cost, and lifespan — with a decision matrix.",
    date: "2026-01-05",
    readTime: "8 min read",
    category: "Comparison",
    cover:
      "https://images.unsplash.com/photo-1771970472779-6452fbce94d6?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "Both XLPE and air bubble insulation are foil-faced, water-resistant, and easy to install. The differences show up in acoustic performance, longevity, and cost per square metre.",
      },
      { type: "h", text: "Head-to-head" },
      {
        type: "ul",
        items: [
          "Thermal (K-value): XLPE 0.032 W/mK — beats bubble 0.040 W/mK",
          "Acoustic: XLPE reduces up to 27 dB, bubble ~7 dB",
          "Thickness: XLPE 4–25 mm; bubble 4–10 mm",
          "Lifespan: XLPE 25+ years; bubble 15+ years",
          "Cost: bubble is 30–45% cheaper per sqm",
        ],
      },
      { type: "h", text: "Pick XLPE if" },
      {
        type: "p",
        text: "You have a PEB with metal roofing under monsoon or heavy rain, a cold room, or any project where acoustic damping matters.",
      },
      { type: "h", text: "Pick Air Bubble if" },
      {
        type: "p",
        text: "You need fast, cost-effective radiant heat control on a warehouse, godown, or poultry shed and noise isn't a primary concern.",
      },
    ],
  },
  {
    slug: "how-insulation-reduces-heat",
    title: "How Insulation Actually Reduces Heat (In Plain English)",
    excerpt:
      "Conduction, convection, radiation — and why radiant reflection is the fastest ROI in industrial buildings.",
    date: "2025-12-28",
    readTime: "5 min read",
    category: "Fundamentals",
    cover:
      "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "Heat moves three ways: through solids (conduction), through fluids (convection), and through empty space as infrared radiation. In a metal-roofed industrial building, radiation is the villain — the sun beats down on the sheet and re-radiates as infrared into your workspace.",
      },
      { type: "h", text: "The three defences" },
      {
        type: "ul",
        items: [
          "Reflect: foil surfaces bounce infrared back outside",
          "Resist: low-conductivity cores (bubble/XLPE) slow whatever gets through",
          "Ventilate: an air gap above the insulation carries away trapped heat",
        ],
      },
      {
        type: "p",
        text: "The reason foil-faced insulation is so effective is that it handles all three. Foil reflects, the core resists, and a properly designed 25 mm air gap lets residual heat vent to atmosphere.",
      },
    ],
  },
  {
    slug: "best-insulation-for-warehouses",
    title: "The Best Insulation for Warehouses in India (2026 Update)",
    excerpt:
      "What warehouse owners in Bihar, UP and beyond should specify — and why cheap options cost more.",
    date: "2025-12-20",
    readTime: "7 min read",
    category: "Industry",
    cover:
      "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "An un-insulated warehouse in North India routinely hits 45–48°C internal temperature by mid-May. Workers slow down, packaged goods degrade, and cooling costs explode. Insulation is not an upgrade — it's an operating cost decision.",
      },
      { type: "h", text: "The Thermo Edge stack" },
      {
        type: "ul",
        items: [
          "6mm double-side foil bubble as the primary reflective layer",
          "XLPE 8mm where acoustics matter",
          "50 mm foil tape on every seam, no exceptions",
          "25 mm air gap above the insulation, always",
        ],
      },
      {
        type: "p",
        text: "Payback in most 10,000 sqft warehouses is 12–18 months in electricity savings alone.",
      },
    ],
  },
  {
    slug: "best-insulation-for-poultry-farms",
    title: "Best Insulation for Poultry Farms — Comfort, FCR & ROI",
    excerpt:
      "The right roof insulation reduces mortality, improves feed conversion, and pays back within one flock cycle.",
    date: "2025-12-12",
    readTime: "6 min read",
    category: "Industry",
    cover:
      "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "Broilers under heat stress eat less, gain slower, and die more. Every degree above 32°C in shed temperature costs measurable profit. Foil bubble insulation on the roof drops shed temperature by 8–12°C on peak days.",
      },
      { type: "h", text: "What we install for poultry" },
      {
        type: "ul",
        items: [
          "Double-side foil air bubble, 4–6 mm",
          "Foil tape on every joint",
          "Ridge ventilation preserved",
          "No fibreglass — chickens hate the dust",
        ],
      },
    ],
  },
  {
    slug: "insulation-buying-guide",
    title: "The Thermo Edge Insulation Buying Guide",
    excerpt:
      "A 10-point checklist to specify insulation like a professional — no matter which vendor you buy from.",
    date: "2025-11-30",
    readTime: "9 min read",
    category: "Guide",
    cover:
      "https://images.unsplash.com/photo-1784739893954-b64adc267d62?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    body: [
      {
        type: "p",
        text: "Insulation is a category flooded with claims. This checklist cuts through them.",
      },
      {
        type: "ul",
        items: [
          "Ask for a material data sheet, not a brochure",
          "Confirm foil purity — pure aluminium beats metallised film",
          "Reflectivity ≥ 95%, emissivity ≤ 0.05",
          "Fire rating in writing (Class B minimum)",
          "Water absorption below 1%",
          "Certified thickness within ±5%",
          "Roll length specified — no partial rolls",
          "Foil tape included in the quote",
          "Installation warranty separate from material warranty",
          "Free site inspection before quotation",
        ],
      },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
